import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import CreateInviteForm from '../components/CreateInviteForm';
import { CopyButton } from '../components/CopyButton';
import { useToastContext } from '../hooks/useToastContext';
import styles from './HomePage.module.css';

/**
 * Главная страница с формой создания приглашения
 *
 * Используется для создания приглашений через веб-интерфейс
 * В продакшене обычно создание происходит через Telegram-бота
 */
const HomePage = () => {
  const [createdInviteId, setCreatedInviteId] = useState<string | null>(null);
  const { showSuccess } = useToastContext();

  const handleInviteCreated = (inviteId: string) => {
    setCreatedInviteId(inviteId);
    showSuccess('Приглашение успешно создано!');
  };

  const inviteUrl = useMemo(() => {
    if (!createdInviteId) {
      return '';
    }
    return `${window.location.origin}/invite/${createdInviteId}`;
  }, [createdInviteId]);

  const handleCopy = () => {
    showSuccess('Ссылка скопирована в буфер обмена!');
  };

  return (
    <div className={styles.container}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className={styles.content}
      >
        <motion.h1
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className={styles.title}
        >
          Создать свадебное приглашение
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className={styles.subtitle}
        >
          Заполните форму ниже, чтобы создать уникальное приглашение
        </motion.p>

        {createdInviteId ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className={styles.successMessage}
          >
            <h2>Приглашение успешно создано!</h2>
            <p>Ваша ссылка на приглашение:</p>
            <div className={styles.linkContainer}>
              <a href={`/invite/${createdInviteId}`} className={styles.inviteLink}>
                {inviteUrl}
              </a>
              <CopyButton text={inviteUrl} onCopy={handleCopy} />
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <CreateInviteForm onSuccess={handleInviteCreated} />
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default HomePage;
